import { useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { startQcCorrection, type QcOpenReport } from "@/lib/manufacturing/qcDashboardClient";
import { queueBuilderAssistantSession } from "@/lib/manufacturing/builderAssistantOpenSession";
import { requestWorkspaceMode } from "@/lib/workspaceModeNavigation";
import { QcDashboardFold } from "./QcDashboardFold";
import { QcReportDeleteButton } from "./QcReportDeleteButton";
import { QcListFilter, QcListPager, useQcListControls } from "./qcListControls";

function when(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
}

export function QcDashboardOpenReports({
  rows,
  reportCount,
  defectCount,
  onDeleted,
}: {
  rows: QcOpenReport[];
  reportCount: number;
  defectCount: number;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const list = useQcListControls(rows, (row, query) =>
    [row.serial, row.model, row.size].join(" ").toLowerCase().includes(query),
  );

  async function correct(sessionId: string) {
    setError(null);
    const res = await startQcCorrection(sessionId);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    queueBuilderAssistantSession(res.data.session_id);
    requestWorkspaceMode({ mode: "mfgBuilderAssistant" });
  }

  return (
    <QcDashboardFold title="Open reports" defaultOpen={false}>
      <p className="text-sm text-gray-900 dark:text-zinc-100">
        {reportCount} submitted {reportCount === 1 ? "report" : "reports"} still have uncorrected failures · {defectCount} open {defectCount === 1 ? "defect" : "defects"}
      </p>
      <QcListFilter value={list.query} onChange={list.setQuery} placeholder="Filter serial or model" />
      {list.total === 0 ? <p className="text-sm text-gray-900 dark:text-zinc-100">No submitted report has an open failure.</p> : null}
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      <ul className="space-y-2">
        {list.rows.map((row) => (
          <li key={row.session_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-300 px-3 py-2 text-sm text-gray-900 dark:border-red-800 dark:text-zinc-100">
            <span>
              <span className="font-medium">{row.serial || "No serial"}</span>
              {row.model ? <span> · {row.model}</span> : null}
              {row.size ? <span> · {row.size}</span> : null}
              <span> · {row.open_count} open{when(row.completed_at) ? ` · ${when(row.completed_at)}` : ""}</span>
            </span>
            <span className="contents">
              <button type="button" className={ghostBtnClass} onClick={() => void correct(row.session_id)}>
                Correct
              </button>
              <QcReportDeleteButton
                sessionId={row.session_id}
                label={row.serial || "this QC report"}
                onDeleted={onDeleted}
              />
            </span>
          </li>
        ))}
      </ul>
      <QcListPager page={list.page} pages={list.pages} onPage={list.setPage} />
    </QcDashboardFold>
  );
}
